<template>
    <div
        :class="{
            'w-full sm:max-w-[477px]': withinModal,
        }"
    >
        <div>
            <BankDepositModal
                :order-item-ids="targetItemIds"
                :order-payment-id="props.orderPaymentId"
                :payment-amount="currentPaymentAmount"
                :payment-label="paymentLabel"
                @payment:success="handlePaymentSuccess"
            />
            <div
                v-if="loadError"
                class="flex flex-col gap-3"
            >
                <MuvalBanner
                    type="warning"
                    icon="system-alert"
                >
                    Couldn't load the payment form. Check your connection and try again.
                </MuvalBanner>
                <MuvalButton
                    primary
                    alternate
                    class="w-full sm:w-fit"
                    @click="initStripe"
                >
                    Retry
                </MuvalButton>
            </div>
            <form
                v-else-if="!success"
                id="payment-form"
                @submit="handleSubmit"
            >
                <div class="flex flex-col gap-5">
                    <div class="text-muval-gray-1 text-sm">
                        <slot
                            name="info"
                            v-bind="{ payment: orderPayment }"
                        ></slot>
                    </div>
                    <CheckoutPriceBreakdown
                        v-if="showPriceBreakdown"
                        :order-item-ids="targetItemIds"
                    />
                    <div
                        v-if="!isCardless"
                        id="payment-element"
                        class="min-h-[150px]"
                        :class="{
                            'max-w-[477px]': withinModal,
                        }"
                    >
                        <!-- Mount the Payment Element here -->
                    </div>
                    <div class="items-top my-2 flex gap-2">
                        <input
                            id="terms"
                            v-model="termsBox"
                            type="checkbox"
                            class="rounded-muval-1 border-muval-gray-3 mt-1 cursor-pointer text-black focus:ring-0 focus:outline-hidden"
                        />
                        <label
                            for="terms"
                            class="text-muval-gray-2 text-xs"
                        >
                            By proceeding you confirm that you have read and agree to the
                            <router-link
                                :to="{ name: 'TermsAndConditions' }"
                                class="text-muval-gray-1 underline"
                            >
                                terms and conditions</router-link
                            >

                            <template v-if="session.isSelectedMatchWithinFinalPaymentWindow">
                                including the
                                <router-link
                                    :to="{ name: 'PrivacyPolicy' }"
                                    class="text-muval-gray-1 underline"
                                >
                                    privacy policy
                                </router-link>
                                relating to short notice bookings
                            </template>
                            <template v-if="isCardless">and confirm your booking request.</template>
                            <template v-else>
                                and authorise us to charge your card in accordance with the payment schedule.
                            </template>
                        </label>
                    </div>
                    <div class="flex flex-col items-stretch gap-4 sm:flex-row sm:items-center">
                        <MuvalButton
                            id="submit"
                            :loading="isLoading"
                            primary
                            alternate
                            :disabled="!termsBox"
                            class="w-full"
                        >
                            <div
                                id="spinner"
                                class="spinner hidden"
                            ></div>
                            <span id="button-text">{{ payButtonText }}</span>
                        </MuvalButton>
                    </div>
                    <span
                        v-if="storageBillingNotice"
                        class="text-muval-gray-3 w-fit text-xs"
                    >
                        {{ storageBillingNotice }}
                    </span>
                    <Tippy
                        v-if="session?.order?.card_fee_string && !isCardless"
                        trigger="mouseenter click"
                        animation="scale"
                        placement="auto"
                        class="-mt-4 w-fit"
                    >
                        <slot name="default">
                            <span class="text-muval-gray-3 w-fit text-xs">Card fees may apply</span>
                        </slot>
                        <template #content>
                            <slot name="content">
                                <div
                                    class="rounded-muval-1 shadow-muval-1 relative border border-gray-200 bg-white px-4 py-3"
                                >
                                    <p class="font-body text-xs">{{ session.order.card_fee_string }}</p>
                                </div>
                            </slot>
                        </template>
                    </Tippy>
                    <span
                        v-if="showBankDeposit && !isCardless"
                        class="text-sm text-[#9B9B9B]"
                    >
                        Alternatively,
                        <a
                            class="link text-[#9B9B9B]"
                            @click.prevent="handleBank"
                        >
                            pay by bank deposit
                        </a>
                    </span>
                    <MuvalBanner
                        v-if="message"
                        type="warning"
                        icon="system-alert"
                        inline
                    >
                        {{ message }}
                    </MuvalBanner>
                </div>
            </form>
            <div v-if="success"></div>
        </div>
    </div>
</template>

<script setup>
import * as Sentry from '@sentry/vue';
import { loadStripe } from '@stripe/stripe-js';
import { useAppStore, useSessionStore, usePaymentStore } from '@/store';
import MuvalButton from '@/components/button/MuvalButton.vue';
import CheckoutPriceBreakdown from '@/components/stripe/CheckoutPriceBreakdown.vue';
import { formatStorageDate } from '@/composables/usePriceBreakdown';

const app = useAppStore();
const session = useSessionStore();
const payment = usePaymentStore();
const props = defineProps({
    serviceType: {
        type: String,
        default: null,
    },
    orderPaymentId: {
        type: [String, Number, null],
        default: null,
    },
    orderItemId: {
        type: [String, Number, null],
        required: false,
        default: null,
    },
    // Several services confirmed together (the pay-quoted-services window). Wins over `orderItemId`.
    orderItemIds: {
        type: Array,
        required: false,
        default: () => [],
    },
    confirmText: {
        type: String,
        default: 'Send booking request',
    },
    paymentAmount: {
        type: String,
        default: null,
        required: false,
    },
    paymentLabel: {
        type: String,
        required: false,
        default: '',
    },
    withinModal: {
        type: Boolean,
        required: false,
        default: true,
    },
    showPriceBreakdown: {
        type: Boolean,
        required: false,
        default: true,
    },
    allowCardless: {
        type: Boolean,
        required: false,
        default: false,
    },
    // Replaces the booking confirm once the card is set up: called with the SetupIntent, it returns
    // what `session.confirmBooking` returns (an error object on failure). The single-service view
    // passes it to pay for one service without confirming the rest of the order.
    onConfirm: {
        type: Function,
        required: false,
        default: null,
    },
});

// Card-less mode: nothing is due at reservation and the backend has flagged
// the order as confirmable without card details (opted-in company, local
// variable job, $0 due now). Skips Stripe entirely.
const isCardless = computed(() => props.allowCardless && session.order?.allows_cardless_confirmation === true);

const termsBox = ref(false);

const emit = defineEmits(['payment:success', 'payment:error', 'payment:intent']);

function addScripts() {
    const userMeta = document.createElement('meta');
    userMeta.setAttribute('name', 'viewport');
    userMeta.setAttribute('content', 'width=device-width, initial-scale=1.0, user-scalable=no');
    document.head.appendChild(userMeta);
}

const appearance = {
    theme: 'flat',
    variables: {
        fontFamily: ' "Roboto", sans-serif',
        fontLineHeight: '1.5',
        borderRadius: '4px',
        colorBackground: '#F6F8FA',
        colorPrimaryText: '#262626',
        colorTextPlaceholder: '#999999',
    },
    rules: {
        '.Block': {
            backgroundColor: 'var(--colorBackground)',
            boxShadow: 'none',
            padding: '12px',
        },
        '.Input': {
            paddingTop: '8px',
            paddingRight: '14px',
            paddingBottom: '8px',
            paddingLeft: '14px',
            fontSize: '14px',
            lineHeight: '30px',
            boxShadow: 'none',
            borderWidth: '1px',
            borderColor: '#d0d0d0',
            borderStyle: 'solid',
        },
        '.Input:hover': {
            boxShadow: 'none',
        },
        '.Input:focus': {
            borderColor: '#000000',
            boxShadow: 'none',
        },
        '.Input:disabled, .Input--invalid:disabled': {
            color: 'lightgray',
        },
        '.Tab': {
            padding: '10px 12px 8px 12px',
            border: 'none',
        },
        '.Tab:hover': {
            border: 'none',
            boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 7px rgba(18, 42, 66, 0.04)',
        },
        '.Tab--selected, .Tab--selected:focus, .Tab--selected:hover': {
            border: 'none',
            backgroundColor: '#fff',
            boxShadow:
                '0 0 0 1.5px var(--colorPrimaryText), 0px 1px 1px rgba(0, 0, 0, 0.03), 0px 3px 7px rgba(18, 42, 66, 0.04)',
        },
        '.Label': {
            fontWeight: '500',
            fontSize: '14px',
            paddingBottom: '4px',
            marginTop: '8px',
        },
        '.Error': {
            fontSize: '12px',
            marginTop: '8px',
        },
    },
    terms: {
        card: 'never',
    },
};
const success = ref(false);
const paymentError = ref(false);
const message = ref(null);
const isLoading = ref(true);
const loadError = ref(false);
const clientSecret = ref(null);

let stripe;
let elements;
let paymentElement;
let disposed = false;

const orderPayment = ref(null);

function teardownStripeElements() {
    try {
        paymentElement?.unmount();
    } catch {
        // Element may already be detached (route change / DOM teardown).
    }
    paymentElement = null;
    elements = null;
}

const currentPaymentAmount = computed(() => {
    if (orderPayment.value) {
        return orderPayment.value.amount.formatted;
    } else {
        return props.paymentAmount;
    }
});

const isStorage = (item) => String(item?.productable_type ?? '').toLowerCase() === 'storage';

// The items this checkout was opened for: the listed ids, else the one item, else none (the
// Request-booking path).
const targetItemIds = computed(() => {
    if (props.orderItemIds?.length) {
        return props.orderItemIds;
    }

    return props.orderItemId != null ? [props.orderItemId] : [];
});

// Sent to `confirmBooking`: the target items, or `null` on the Request-booking path.
const targetItemsPayload = computed(() =>
    targetItemIds.value.length ? targetItemIds.value.map((id) => ({ id })) : null,
);

// The services this checkout confirms: the items it was opened for, or on the Request-booking
// path (no item) every unconfirmed service on the order. An `orderPaymentId` mount (an instalment
// or a retry) confirms nothing; it pays an existing payment, which is charged now.
const itemsBeingConfirmed = computed(() => {
    if (props.orderPaymentId) {
        return [];
    }

    const items = session?.order?.items ?? [];
    if (targetItemIds.value.length) {
        return items.filter((i) => targetItemIds.value.some((id) => id == i?.id));
    }

    return items.filter((i) => i?.status !== 'CONFIRMED');
});

// Storage is not charged at confirmation: its subscription is billed from its start date. So a
// checkout confirming storage must not show a "Pay" amount (CU-14zcqnthqvc).
const confirmingStorageItems = computed(() => itemsBeingConfirmed.value.filter(isStorage));

const confirmingOnlyStorage = computed(
    () => confirmingStorageItems.value.length > 0 && itemsBeingConfirmed.value.every(isStorage),
);

// Storage whose start date has arrived is charged at confirmation for the cycles already elapsed
// (`ConfirmStorageProduct` + `StorageArrearsCharge`, CU-86d3nb7d5). The API measures "arrived" as
// today reaching the start date's day in the storage's own timezone, falling back to
// Australia/Sydney, so the same rule is applied here.
function storageHasStarted(item) {
    const start = item?.product?.start_date;
    if (!start) {
        return false;
    }

    const timeZone = item?.product?.timezone?.timezone || 'Australia/Sydney';
    try {
        const day = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' });
        return day.format(new Date(start)) <= day.format(new Date());
    } catch {
        return false;
    }
}

const storageBillingNotice = computed(() => {
    if (!confirmingStorageItems.value.length) {
        return null;
    }

    // Never say storage is not charged today when some of it is. The API sends the up-front amount
    // for storage that has started (`upfront_amount`), summed from the same `.formatted` figures the
    // breakdown shows so the two agree under ex-tax display. The date check is the fallback for a
    // payload without the field.
    const upfrontCents = confirmingStorageItems.value.reduce((acc, item) => {
        const value = parseFloat(String(item?.product?.upfront_amount?.formatted ?? '').replace(/[$,\s]/g, ''));
        return Number.isFinite(value) ? acc + Math.round(value * 100) : acc;
    }, 0);
    if (upfrontCents > 0) {
        const amount = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(
            upfrontCents / 100,
        );
        return `Storage that has already started is charged ${amount} today for the time since its start date.`;
    }
    if (confirmingStorageItems.value.some(storageHasStarted)) {
        return 'Storage that has already started is charged today for the time since its start date.';
    }

    const notCharged = 'You will not be charged for storage today.';
    // `formatStorageDate` returns '-' for a missing or unparseable date. Name a date only when every
    // storage service has a real one: a dated notice beside an undated service would claim it too.
    const dates = confirmingStorageItems.value.map((item) =>
        formatStorageDate(item?.product?.start_date, item?.product?.timezone?.timezone),
    );

    if (dates.includes('-')) {
        return notCharged;
    }
    if (new Set(dates).size === 1) {
        return `${notCharged} It is billed from ${dates[0]}.`;
    }
    return `${notCharged} Each storage service is billed from its start date.`;
});

const payButtonText = computed(() => {
    // A cardless reservation takes no card at all, so it keeps its own wording.
    if (isCardless.value) {
        return props.confirmText;
    }
    if (confirmingOnlyStorage.value) {
        return 'Confirm subscription';
    }
    if (confirmingStorageItems.value.length) {
        return 'Confirm booking';
    }
    return currentPaymentAmount.value ? `Pay ${currentPaymentAmount.value}` : props.confirmText;
});

const initStripe = async () => {
    loadError.value = false;
    isLoading.value = true;
    try {
        stripe = await loadStripe(`${import.meta.env.VITE_STRIPE_KEY}`, {
            stripeAccount: app.tenant.company.settings.stripe_id,
        });
        // Stripe.js resolves null (without rejecting) when blocked by an extension.
        if (!stripe) throw new Error('Failed to load Stripe.js');
        if (disposed) return;
        await initElements();
    } catch (err) {
        if (disposed) return;
        loadError.value = true;
        Sentry.captureException(err);
    } finally {
        if (!disposed) {
            isLoading.value = false;
        }
    }
};

onMounted(async () => {
    emit('payment:intent');
    addScripts();

    if (isCardless.value) {
        isLoading.value = false;
        return;
    }

    await initStripe();
});

onUnmounted(() => {
    disposed = true;
    teardownStripeElements();
    stripe = null;
});

const initElements = async () => {
    const { payment: paymentResponse, clientSecret: clientSecretResponse } = await payment.retrievePaymentSecret(
        props.orderPaymentId,
    );

    if (disposed) return;

    clientSecret.value = clientSecretResponse;
    orderPayment.value = paymentResponse;

    teardownStripeElements();

    elements = stripe.elements({
        clientSecret: clientSecret.value,
        appearance: appearance,
    });

    await elements.update({ locale: 'au' });

    if (disposed) {
        teardownStripeElements();
        return;
    }

    paymentElement = elements.create('payment', {
        terms: { auBecsDebit: 'never', card: 'never' },
    });

    paymentElement.mount('#payment-element');
};

const handleBank = async () => {
    try {
        app.openBankDepositModal();
    } catch (error) {
        console.log('error', error);
    }
};

const handlePaymentSuccess = () => {
    emit('payment:success');
};

const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLoading.value) {
        return;
    }

    if (!termsBox.value) {
        return;
    }

    isLoading.value = true;

    try {
        message.value = null;

        if (isCardless.value) {
            await confirmCardless();
        } else {
            await confirmSetup();
        }

        if (paymentError.value) {
            emit('payment:error');
        } else {
            handlePaymentSuccess();
        }
    } catch (error) {
        if (error.message) {
            message.value = error.message;
        } else {
            message.value = 'An unexpected error occurred.';
        }
    } finally {
        isLoading.value = false;
    }
};

async function confirmCardless() {
    paymentError.value = false;

    const confirmError = await session.confirmBooking(null, targetItemsPayload.value);

    if (typeof confirmError === 'object') {
        // A 422 here means eligibility changed under us (e.g. the removalist turned off
        // card-less confirmation mid-session), so card details are now required. Show a
        // clear next step instead of Laravel's raw "The seti id field is required." copy.
        if (confirmError.code === 422) {
            message.value =
                'Card details are now required to confirm this booking. Please reopen the payment step and enter your card.';
        } else {
            message.value = confirmError.message;
        }
        paymentError.value = true;
    }

    session.retrieve();
}

async function confirmSetup() {
    const result = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
    });

    if (result.error) {
        message.value = null;
        paymentError.value = true;

        payment.cardDeclined();

        if (result.error.type === 'card_error' || result.error.type === 'validation_error') {
            message.value = result.error.message;
        } else {
            message.value = 'An unexpected error occurred.';
        }
    }

    if (result.setupIntent) {
        if (props.orderPaymentId) {
            try {
                await payment.retrySetup(props.orderPaymentId, result.setupIntent);
            } catch (error) {
                console.log('Error retrying setup', error);

                if (typeof error === 'object') {
                    message.value = error.message;
                    paymentError.value = true;

                    initElements();
                }
            }
        } else {
            let confirmError =
                props.onConfirm ?
                    await props.onConfirm(result.setupIntent)
                :   await session.confirmBooking(result.setupIntent, targetItemsPayload.value);

            if (typeof confirmError === 'object') {
                message.value = confirmError.message;
                paymentError.value = true;

                initElements();
            }
        }

        session.retrieve();
    }
}

const orderCarriesQuotedStorage = computed(() => {
    // Only the booking-confirmation mounts reach `storeBank`, which is what rejects an order
    // carrying QUOTED storage. The `orderPaymentId` mounts — PaymentPage instalments and
    // PaymentTimeline retries — go through `payment.flagBankDeposit` instead and must keep
    // their bank-deposit option.
    if (props.orderPaymentId) {
        return false;
    }

    const items = session?.order?.items ?? [];
    return items.some(
        (item) =>
            String(item?.productable_type ?? '').toLowerCase() === 'storage' &&
            String(item?.service_status ?? '').toUpperCase() === 'QUOTED',
    );
});

const showBankDeposit = computed(() => {
    // A custom confirm pays by card only: the bank-deposit path confirms through the booking.
    return (
        !props.onConfirm &&
        (app.tenant?.company?.settings?.show_bank_deposit ?? false) &&
        app.tenantBankAccount != null &&
        !orderCarriesQuotedStorage.value
    );
});
</script>
